import { lazy, Suspense } from "react";
import { createBrowserRouter, Outlet } from "react-router";
import { Authenticated, AuthLoading, Unauthenticated } from "convex/react";
import { useAtomValue } from "jotai";

import {
  wallpaperAtom,
  wallpaperHasBackdrop,
  wallpaperStyle,
} from "@/shell/wallpaperState";
import { BottomBar, CockpitShell } from "@/shell";
import { TitleBar } from "@/shell/TitleBar";
import { SideRail } from "@/shell/SideRail";
import { SettingsModal } from "@/shell/SettingsModal";
import { ErrorBoundary, RouterErrorBoundary } from "@/core/components";
import { PaymentSoundListener } from "@/core/pwa/usePaymentSound";
import { PaymentToasts } from "@/shell/PaymentToasts";
import { PwaUpdateToast } from "@/core/updater/PwaUpdateToast";
import { UpdaterToast } from "@/core/updater/UpdaterToast";
import { ChangelogModal } from "@/core/changelog/ChangelogModal";
import {
  AuthLoadingScreen,
  LoginScreen,
  MemberProfileGate,
  OrgGate,
  UserSync,
} from "@/core/auth";
import { RegieTestCandidatePage } from "@/regie-test/RegieTestCandidatePage";
import { RegieRecruitmentLanding } from "@/regie-test/RegieRecruitmentLanding";
import { RegieTestAdminGate } from "@/regie-test/RegieTestAdminGate";
import { VenteApercuGate } from "@/regie-test/VenteApercuGate";
import { AdresseInconnue } from "@/core/components";
import { MARQUE } from "@dozka/ui";

/**
 * Frame applicatif persistant — TitleBar + BottomBar visibles dans TOUS les
 * etats (login, agency onboarding, creator onboarding, cockpit, docs). Le
 * contenu central change selon la route, mais les barres ne bougent pas pour
 * que l'user puisse toujours acceder a :
 *   - son profil (signOut, settings) — UserBadge dans TitleBar
 *   - la langue, le theme, le workspace — BottomBar
 *   - les controles fenetre Tauri (min/max/close)
 *
 * Pre-pivot (avant 2026-05-17) les barres etaient dans CockpitShell, donc
 * masquees pendant login/onboarding (mauvaise UX). Pre-pivot doc (2026-05-22)
 * la route /docs etait un sibling, donc les barres etaient cachees aussi
 * sur la doc — meme probleme. Reglee en faisant de ChromeLayout un layout
 * route avec <Outlet />.
 */
function ChromeLayout() {
  // Fond d'écran « plein écran » façon Windows : on peint le wallpaper en
  // couche de fond DERRIÈRE tout le chrome. Les barres (TitleBar/SideRail/
  // BottomBar) sont en verre dépoli translucide → elles laissent voir le
  // wallpaper flouté derrière (et non les panneaux, qui restent opaques, posés
  // dessus comme des fenêtres). La classe `cockpit-has-wallpaper` rend les
  // gouttières (`.cockpit-canvas`) transparentes pour que le fond transparaisse
  // entre les panneaux (cf globals.css).
  const wallpaper = useAtomValue(wallpaperAtom);
  const hasWallpaper = wallpaperHasBackdrop(wallpaper);

  return (
    <div
      className={
        // 🪤 PAS `h-dvh` : une unité de viewport ignore le `zoom` de la taille
        // d'affichage → bande vide en bas à 80 %, shell coupé à 125 %.
        // Divisée par le facteur, la hauteur est exacte dans les deux sens.
        "relative flex h-[calc(100dvh/var(--zoom-affichage,1))] w-full flex-col overflow-hidden bg-canvas text-fg" +
        (hasWallpaper ? " cockpit-has-wallpaper" : "")
      }
    >
      {hasWallpaper && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={wallpaperStyle(wallpaper)}
        />
      )}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <TitleBar />
        <div className="flex min-h-0 flex-1">
          {/* Rail vertical gauche (desktop) : docs, actualité, layout, langue,
              thème. Se masque seul sur mobile. */}
          <SideRail />
          <main className="relative min-h-0 flex-1 overflow-hidden">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
        <BottomBar />
      </div>
      {/* Modale Settings — montee au niveau du chrome pour rester accessible
          depuis n'importe quelle route (cockpit ET /docs). Si on la laissait
          dans CockpitShell, cliquer "Settings" depuis la page docs ne
          declencherait rien visuellement (atom passe a true mais aucun modal
          dans le DOM pour s'ouvrir). */}
      <SettingsModal />
      {/* Toast auto-update Tauri. Self-guarded : ne rend rien hors Tauri /
          quand l'updater n'est pas configure / quand pas d'update dispo. */}
      <UpdaterToast />
      {/* Toast PWA browser : nouveau bundle dispo apres deploy Dokploy.
          Self-guarded : ne rend rien tant que useRegisterSW ne signale pas
          needRefresh (donc inactif en dev + dans Tauri). */}
      <PwaUpdateToast />
      {/* Le feed de toasts de paiement vit désormais sous <Authenticated>, à
          côté du listener : il ouvre la conversation du fan au clic, et ce
          geste (verrou, heartbeat) a besoin de la session. */}
      {/* Modal "What's new" — Tauri only, montre les highlights apres update.
          Self-guarded via useChangelog. */}
      <ChangelogModal />
    </div>
  );
}

/**
 * Auth gate — branche le user vers loading / login / cockpit selon son etat
 * Convex. Rendu comme route enfant de ChromeLayout pour heriter de la
 * TitleBar + BottomBar.
 */
function AuthGate() {
  return (
    <>
      <AuthLoading>
        <AuthLoadingScreen />
      </AuthLoading>
      <Unauthenticated>
        <LoginScreen />
      </Unauthenticated>
      <Authenticated>
        <UserSync />
        {/* Joue le SFX de paiement (cockpit ouvert) — feed temps réel Convex. */}
        <PaymentSoundListener />
        {/* Feed de toasts de paiement in-app (« ka-ching » visuel, desktop) :
            cliquable, il ouvre la conversation du fan. Portal vers body. */}
        <PaymentToasts />
        <OrgGate>
          <MemberProfileGate>
            <CockpitShell />
          </MemberProfileGate>
        </OrgGate>
      </Authenticated>
    </>
  );
}

/** La landing de recrutement Cyrano, en chunk à part : son CSS, ses polices
 *  et ses écrans n'arrivent qu'avec elle, jamais dans le cockpit. */
const RejoindreLanding = lazy(() => import("@/rejoindre/RejoindreLanding"));
const rejoindre = (
  <Suspense fallback={<div style={{ minHeight: "100dvh", background: "#05070f" }} />}>
    <RejoindreLanding />
  </Suspense>
);

/** `cyrano.team` (et `www.`) n'est QUE la landing : sa racine est la page,
 *  pas le cockpit. L'espace de travail des chatteurs est sur `cyrano.work`
 *  (Kayla, 13-10-2026). */
const SUR_CYRANO = /^(www\.)?cyrano\.team$/i.test(window.location.hostname);

/** `app.rishfans.com` n'est PAS le cockpit : c'est l'espace partenaire seul,
 *  sans le chrome (rail, barre du bas, Bureau) ni l'écran de connexion Dozka
 *  (Kayla, 13-10-2026). En chunk à part, comme la landing Cyrano. */
const SUR_RISHFANS = MARQUE.id === "rishfans";
const EspaceRishfans = lazy(() =>
  import("@/rishfans/EspaceRishfans").then((m) => ({ default: m.EspaceRishfans })),
);

export const router = createBrowserRouter([
  ...(SUR_CYRANO ? [{ path: "/", element: rejoindre }] : []),
  // La landing n'existe QUE sur `cyrano.team` — jamais sur `dozka.app`
  // (Kayla, 13-10-2026 : « retire-moi cette page de dozka.app »). En dev,
  // `/rejoindre` la montre sur localhost ; le build de prod retire la route.
  ...(import.meta.env.DEV ? [{ path: "/rejoindre", element: rejoindre }] : []),
  {
    // Landing publique du funnel recrutement. Elle crée un lien de test unique
    // puis redirige vers le parcours candidat, sans AuthGate ni chrome cockpit.
    path: "/candidature",
    element: <RegieRecruitmentLanding />,
  },
  // Sur `cyrano.team`, pas de cockpit du tout : sa route `/` (avec son enfant
  // `index`) passe AVANT une route `/` simple au classement de React Router,
  // et la racine rendait l'écran de connexion au lieu de la landing (13-10).
  ...(SUR_RISHFANS
    ? [
      {
        path: "/",
        element: (
          <Suspense fallback={<div style={{ minHeight: "100dvh", background: "#000" }} />}>
            <EspaceRishfans />
          </Suspense>
        ),
        errorElement: <RouterErrorBoundary />,
      },
    ]
    : []),
  ...(SUR_CYRANO || SUR_RISHFANS
    ? []
    : [
      {
        path: "/",
        element: <ChromeLayout />,
        // Errors thrown au-dessus du <Outlet> (TitleBar, BottomBar, SideRail,
        // hooks Convex appelés par AuthGate, etc.) ne sont PAS catchés par le
        // <ErrorBoundary> intra-ChromeLayout — c'est le router-level qui les
        // attrape via errorElement. RouterErrorBoundary détecte les ConvexError
        // {code:"UNAUTHENTICATED" | "USER_NOT_SYNCED" | "NOT_A_MEMBER"} et
        // propose un re-login propre au lieu de l'écran « Unexpected Application
        // Error! » avec stack JS brute.
        errorElement: <RouterErrorBoundary />,
        children: [
          { index: true, element: <AuthGate /> },
          {
            // Route consacree au flow OAuth desktop. Quand AuthKit (cote webview
            // Tauri) est configure avec redirectUri = /auth/desktop-callback,
            // WorkOS redirige ici apres auth. Le webview Tauri y arrive via
            // dozka:// → window.location (cf src-tauri/src/lib.rs on_open_url)
            // avec ?code=... AuthKitProvider detecte le code en URL et le echange
            // automatiquement, puis on tombe dans Authenticated et on render le
            // shell.
            //
            // Cas browser → ce path est intercepte AVANT React dans main.tsx pour
            // hand off via dozka:// (le browser ne doit jamais echanger le code,
            // sinon l'app Tauri reste deconnectee).
            path: "auth/desktop-callback",
            element: <AuthGate />,
          },
        ],
      },
      ]),
  {
    // Test de qualification des décideurs de La Régie — parcours candidat
    // PUBLIQUE (jeton dans l'URL). Route SŒUR de "/", PAS un enfant de
    // ChromeLayout : élément rendu SANS <AuthGate> → publique automatiquement
    // (même patron que /docs), et cette fois carrément hors chrome cockpit
    // (pas de TitleBar/SideRail/BottomBar) — la page est son propre chrome,
    // cf. `regie-test/candidate/PhoneFrame.tsx`.
    path: "/regie-test/:token",
    element: <RegieTestCandidatePage />,
  },
  {
    // Admin Kayla du Test de qualification — protégé WorkOS
    // (`RegieTestAdminGate`) mais sans `CockpitShell` : juste la page admin.
    path: "/regie-test-admin",
    element: <RegieTestAdminGate />,
  },
  {
    // APERÇU de l'épreuve de vente — même gate WorkOS que l'admin, et surtout
    // PAS publique : la consigne est explicite et le scénario est la réponse
    // au test. Rien n'est persisté ici ; c'est l'écran que Kayla joue pour
    // trancher avant qu'on le branche dans le parcours candidat.
    path: "/regie-test-vente",
    element: <VenteApercuGate />,
  },
  {
    // Attrape-tout. Sans lui, une adresse inconnue rendait une page BLANCHE :
    // aucun message, aucun retour, rien à faire. Et le cas le plus fréquent
    // n'est pas la faute de frappe — c'est une route DÉJÀ en ligne que le
    // cockpit du visiteur ne connaît pas encore, son service worker servant
    // l'ancien bundle. `AdresseInconnue` le dit et offre le seul geste qui
    // marche : purger les caches + désenregistrer le SW + recharger.
    path: "*",
    element: <AdresseInconnue />,
  },
]);
